import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";

const POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2500;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalToken = params.get("provider") === "paypal" ? params.get("token") : null;
  const fedapayId = params.get("provider") === "fedapay" ? params.get("id") : null;
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking", paymentStatus: null, orderId: null, error: null });
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId && !paypalToken && !fedapayId) {
      setState({ status: "error", error: "Session de paiement introuvable." });
      return;
    }
    let cancelled = false;

    if (paypalToken || fedapayId) {
      const endpoint = paypalToken
        ? `/payments/paypal/capture/${paypalToken}`
        : `/payments/fedapay/verify/${fedapayId}`;
      api
        .post(endpoint)
        .then(({ data }) => {
          if (cancelled) return;
          if (data.status === "paid") {
            if (!clearedRef.current) { clear(); clearedRef.current = true; }
            setState({ status: "paid", paymentStatus: "paid", orderId: data.order_id });
          } else {
            setState({ status: "timeout", paymentStatus: data.status });
          }
        })
        .catch((e) => {
          if (cancelled) return;
          setState({ status: "error", error: formatApiError(e.response?.data?.detail) || e.message });
        });
      return () => { cancelled = true; };
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;

        if (data.payment_status === "paid") {
          if (!clearedRef.current) { clear(); clearedRef.current = true; }
          setState({ status: "paid", paymentStatus: "paid", orderId: data.order_id });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", paymentStatus: data.payment_status });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= POLL_ATTEMPTS) {
          setState({ status: "timeout", paymentStatus: data.payment_status });
          return;
        }
        setState({ status: "pending", paymentStatus: data.payment_status });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: formatApiError(e.response?.data?.detail) || e.message });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId, paypalToken, fedapayId, clear]);

  return (
    <div data-testid="checkout-success-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
        {state.status === "checking" || state.status === "pending" ? (
          <>
            <div className="overline mb-5">Paiement sécurisé</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F5F5F0] mb-8 leading-[0.95]">Vérification du paiement…</h1>
            <p className="text-[#A3A39D]">Ne fermez pas la page. Nous confirmons votre paiement.</p>
          </>
        ) : state.status === "paid" ? (
          <>
            <div className="w-16 h-16 mx-auto mb-8 border border-[#D4AF37] flex items-center justify-center text-[#D4AF37]">
              <Check strokeWidth={1.25} />
            </div>
            <div className="overline mb-5">Paiement confirmé</div>
            <h1 className="font-serif text-5xl md:text-6xl text-[#F5F5F0] mb-6 leading-[0.95]">Merci.</h1>
            {state.orderId && (
              <p className="text-[#A3A39D] leading-relaxed mb-3">
                Votre commande N° <span className="text-[#D4AF37]">{state.orderId.slice(0, 8).toUpperCase()}</span> est enregistrée.
              </p>
            )}
            <p className="text-[#A3A39D] leading-relaxed mb-10">
              Un email de confirmation vient de vous être envoyé. Notre conciergerie vous contacte sous 24h pour organiser la livraison privée.
            </p>
            <Link to="/boutique" className="btn-primary" data-testid="back-to-shop-btn">Retour à la collection</Link>
          </>
        ) : state.status === "expired" ? (
          <>
            <div className="w-16 h-16 mx-auto mb-8 border border-red-400/60 flex items-center justify-center text-red-400">
              <AlertTriangle strokeWidth={1.25} />
            </div>
            <h1 className="font-serif text-4xl text-[#F5F5F0] mb-6">Session expirée</h1>
            <p className="text-[#A3A39D] mb-10">La session de paiement a expiré. Veuillez recommencer.</p>
            <Link to="/commande" className="btn-primary">Retour au paiement</Link>
          </>
        ) : state.status === "timeout" ? (
          <>
            <h1 className="font-serif text-4xl text-[#F5F5F0] mb-6">Vérification en cours</h1>
            <p className="text-[#A3A39D] mb-10">
              Le paiement prend plus de temps que prévu. Si vous avez bien été débité(e), vous recevrez l'email de confirmation sous peu. Vous pouvez aussi contacter notre conciergerie.
            </p>
            <Link to="/boutique" className="btn-ghost">Retour à la collection</Link>
          </>
        ) : (
          <>
            <h1 className="font-serif text-4xl text-[#F5F5F0] mb-6">Une erreur est survenue</h1>
            <p className="text-[#A3A39D] mb-10">{state.error || "Impossible de vérifier le paiement."}</p>
            <Link to="/commande" className="btn-primary">Réessayer</Link>
          </>
        )}
      </div>
    </div>
  );
}
